import { Badge } from '@repo/ui'
import { SalesChannel } from '@/api/user/profile'
import type { UserOrgProfile } from '@/api/user/profile'
import { SettingsSection } from './ScopedSettings'
import type { ReactNode } from 'react'

export function OrganisationAccountSettings({ userOrgProfile }: { userOrgProfile: UserOrgProfile }) {
  const features = buildFeatureList(userOrgProfile.enabled_features).filter((f) => f.enabled)
  const access = userOrgProfile.org.owner ? 'Owner' : userOrgProfile.org.admin ? 'Administrator' : 'Member'

  return (
    <SettingsSection id="organisation-account-heading" title="Account" summary="Organisation details and billing.">
      <dl className="divide-y rounded-lg border">
        <AccountRow label="Name">{userOrgProfile.org.name}</AccountRow>
        <AccountRow label="Slug">
          <span className="font-mono text-xs">{userOrgProfile.org.slug}</span>
        </AccountRow>
        <AccountRow label="Your access">
          <Badge variant="outline" className="border-primary/30 bg-accent text-primary">
            {access}
          </Badge>
        </AccountRow>
        <AccountRow label="Billing">{formatBilling(userOrgProfile.org.sales_channel)}</AccountRow>
        <AccountRow label="Payment">
          {userOrgProfile.org.payment_active ? (
            <Badge variant="outline" className="border-success/30 text-success">
              Active
            </Badge>
          ) : (
            <Badge variant="outline" className="border-warning/30 text-warning">
              Inactive
            </Badge>
          )}
        </AccountRow>
      </dl>

      {features.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-muted-foreground">Enabled features</h3>
          <ul className="mt-3 flex flex-wrap gap-2">
            {features.map((f) => (
              <li key={f.label}>
                <Badge variant="outline" className="border-success/30 text-success">
                  {f.label}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SettingsSection>
  )
}

function AccountRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-sm font-medium">{children}</dd>
    </div>
  )
}

function formatBilling(channel: string): string {
  switch (channel) {
    case SalesChannel.AwsMarketplace:
      return 'AWS Marketplace'
    case SalesChannel.Direct:
      return 'Invoice'
    default:
      return channel
  }
}

function buildFeatureList(features: UserOrgProfile['enabled_features']) {
  return [
    { label: 'Search Agent', enabled: features.search_agent },
    { label: 'Email Agent', enabled: features.email_agent },
    { label: 'Marketing Agent', enabled: features.marketing_agent },
    { label: 'Content Connectors', enabled: features.content_connectors },
    { label: 'Google Drive Connector', enabled: features.g_drive_connector },
    { label: 'SharePoint Connector', enabled: features.sharepoint_connector },
    { label: 'Web Crawler Connector', enabled: features.webcrawler_connector },
    { label: 'Connector Resync', enabled: features.connector_resync },
    { label: 'Global Content', enabled: features.global_content }
  ]
}
